import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Chip } from '@heroui/react'
import { buttonVariants } from '@heroui/styles'
import { api, type StoredLayout } from '../../lib/api'
import { lagOf } from '../../lib/designReach'
import { deskLabel, problemsAt, retiring, withCount } from '../../lib/equipment'
import { clockIn } from '../../lib/kioskText'
import { designFields, machineDesign, withDesign, type DesignField } from '../../lib/machineDesign'
import type { StaffKey } from '../../lib/staffText'
import type {
  Branch, CounterView, ProfileUnit, ProfileUnitKind, ScreenApp, Unit, UnitInstance, UnitInstances,
} from '../../lib/types'
import { cacheAmount, mediaSkipped, printerFault, screenCacheOf } from '../../lib/units'
import { Icon } from '../../ui/icons'
import { useMayRead, useSession } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { SETUP_CHANGED } from '../setupChanged'
import { FormSection } from '../settingsKit'
import { AskBar } from '../setupKit'
import { DesignPicker } from './MachineDesign'
import type { BranchDraft } from './useBranchDraft'
import '../../styles/equipment.css'
import '../../styles/branchpage.css'
import '../../styles/branchmachines.css'

const REFRESH_MS = 10_000
const MAX = 99

/** Screens first: what the page is mostly visited for is which design each one shows. */
export const MACHINE_ORDER: readonly ProfileUnitKind[] = ['kiosk', 'display', 'desk_display', 'service_point', 'feedback']

type Say = ReturnType<typeof useStaffText>['say']
type Leaving = { code: string; staffName?: string }

const up = (s: string) => s.toUpperCase()

/** The question a removal asks: whoever is signed in at one of them, then the question. */
export function removeQuestion(leaving: Leaving[], say: Say): string {
  const facts = leaving.flatMap((l) => (l.staffName ? [say('eq.inst.signedIn', { who: l.staffName, code: l.code }) + '. '] : []))
  return facts.join('') + say('eq.inst.removeAsk', { codes: leaving.map((l) => l.code).join(', ') })
}

/** Live machines and desks of the branch, read again on a setup change and every 10 s while shown. */
function useLive(branchId: string) {
  const [units, setUnits] = useState<Unit[] | null>(null)
  const [counters, setCounters] = useState<CounterView[] | null>(null)
  const alive = useRef(true)

  const read = useCallback(async () => {
    const [u, s] = await Promise.allSettled([api.units(branchId), api.snapshot(branchId)])
    if (!alive.current) return
    if (u.status === 'fulfilled') setUnits(u.value.units)
    if (s.status === 'fulfilled') setCounters(s.value.counters)
  }, [branchId])

  useEffect(() => {
    alive.current = true
    void read()
    // The snapshot carries the day's tickets: not read while the tab is hidden.
    const readShown = () => {
      if (!document.hidden) void read()
    }
    const timer = window.setInterval(readShown, REFRESH_MS)
    const onChange = () => void read()
    window.addEventListener(SETUP_CHANGED, onChange)
    document.addEventListener('visibilitychange', readShown)
    return () => {
      alive.current = false
      window.clearInterval(timer)
      window.removeEventListener(SETUP_CHANGED, onChange)
      document.removeEventListener('visibilitychange', readShown)
    }
  }, [read])

  return { units, counters, read }
}

/** What the machines list and a machine's page read beside the setup: live state, designs, screen apps. */
export function useMachineData(branch: Branch) {
  const session = useSession()
  const live = useLive(branch.id)
  const [layouts, setLayouts] = useState<StoredLayout[] | null>(null)
  const [apps, setApps] = useState<ScreenApp[]>([])
  const organisation = session?.identity.owner ? branch.tenantId : undefined
  const moduleOn = (session?.identity.modules ?? []).includes('screenApps')
  const readsLayouts = useMayRead('layouts')

  useEffect(() => {
    if (!readsLayouts) return
    api
      .layouts(organisation)
      .then((r) => setLayouts(r.layouts))
      .catch(() => setLayouts([]))
  }, [organisation, readsLayouts])

  useEffect(() => {
    if (!moduleOn) return
    api
      .screenApps(branch.tenantId)
      .then((r) => setApps(r.apps))
      .catch(() => setApps([]))
  }, [branch.tenantId, moduleOn])

  const liveUnit = (code: string) => live.units?.find((u) => u.active && up(u.code) === up(code))
  const liveDesk = (code: string) => live.counters?.find((c) => up(c.code) === up(code))
  return { live, layouts, apps, moduleOn, liveUnit, liveDesk }
}

export type MachineData = ReturnType<typeof useMachineData>

/** The branch's machines by kind: count, the design each shows, and live status. */
export function BranchEquipment({
  branch,
  d,
  canWrite,
  data,
  onOpen,
}: {
  branch: Branch
  d: BranchDraft
  canWrite: boolean
  data: MachineData
  /** Opens one machine's page, by group and instance index in the setup. */
  onOpen: (gi: number, j: number) => void
}) {
  const { say } = useStaffText()
  const [ask, setAsk] = useState<{ key: string; leaving: Leaving[]; apply: () => void } | null>(null)
  const readsProfiles = useMayRead('operationProfiles')
  const { live, layouts, liveUnit, liveDesk } = data

  const draft = d.draft
  const equipment = d.equipment
  if (!draft || !equipment) return null

  const doc = equipment.document
  const unitOf = new Map(doc.units.map((u) => [u.id, u]))
  const codes = draft.units.flatMap((g) => g.instances.map((i) => i.code))
  const deskInstances = draft.units
    .filter((g) => unitOf.get(g.profileUnitId)?.kind === 'service_point')
    .flatMap((g) => g.instances)
  const desks = deskInstances.map((i) => i.code)
  const deskName = (code: string) => deskInstances.find((i) => up(i.code) === up(code))?.name
  const strays = draft.units
    .map((g, gi) => [g, gi] as const)
    .filter(([g]) => !unitOf.has(g.profileUnitId) && g.instances.length > 0)
  const locked = !canWrite || d.busy
  const leavingOf = (removed: UnitInstance[], kind: ProfileUnitKind) => retiring(removed, kind, live.counters ?? [])

  const putGroup = (gi: number, next: UnitInstances) =>
    d.setDraft((x) => (gi >= 0 ? { ...x, units: x.units.map((g, i) => (i === gi ? next : g)) } : { ...x, units: [...x.units, next] }))

  const setCount = (pu: ProfileUnit, gi: number, count: number) => {
    const ui = gi >= 0 ? draft.units[gi]! : { profileUnitId: pu.id, instances: [] }
    // A new desk display or tablet stands at the next counter none of its kind stands at.
    const linked = draft.units
      .filter((g) => unitOf.get(g.profileUnitId)?.kind === pu.kind)
      .flatMap((g) => g.instances.flatMap((i) => (i.desk ? [i.desk] : [])))
    const { next, retiring: removed } = withCount(ui, pu.kind, pu.name, count, codes, { desks, linked })
    if (removed.length === 0) {
      putGroup(gi, next)
      return
    }
    setAsk({ key: pu.id, leaving: leavingOf(removed, pu.kind), apply: () => putGroup(gi, next) })
  }

  const setDesign = (gi: number, j: number, field: DesignField, value: string) =>
    d.setDraft((x) => ({
      ...x,
      units: x.units.map((g, i) =>
        i !== gi ? g : { ...g, instances: g.instances.map((it, k) => (k === j ? withDesign(it, field, value) : it)) },
      ),
    }))

  const dropGroup = (gi: number) => d.setDraft((x) => ({ ...x, units: x.units.filter((_, i) => i !== gi) }))

  const askLine = (key: string) =>
    ask?.key === key && (
      <AskBar
        className="eq-inst__ask"
        title={removeQuestion(ask.leaving, say)}
        actions={
          <>
            <Button variant="ghost" size="sm" autoFocus onPress={() => setAsk(null)}>
              {say('common.keep')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onPress={() => {
                ask.apply()
                setAsk(null)
              }}
            >
              {say('common.remove')}
            </Button>
          </>
        }
      />
    )

  const problemTag = (gi: number, j: number) => {
    const ps = problemsAt(d.problems, `units[${gi}].instances[${j}]`)
    return (
      ps.length > 0 && (
        <Chip size="sm" variant="soft" color="warning" className="eq-inst__problem" title={ps.map((p) => p.message).join('\n')}>
          {ps[0]!.message}
        </Chip>
      )
    )
  }

  const stepper = (pu: ProfileUnit, gi: number, n: number) =>
    canWrite && (
      <span className="eq-step">
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label={say('eq.inst.fewer')}
          isDisabled={locked || n === 0}
          onPress={() => setCount(pu, gi, n - 1)}
        >
          <span aria-hidden="true">−</span>
        </Button>
        <span className="eq-step__n">{n}</span>
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label={say('eq.inst.more')}
          isDisabled={locked || n >= MAX}
          onPress={() => setCount(pu, gi, n + 1)}
        >
          <Icon name="plus" size={16} />
        </Button>
      </span>
    )

  /** A counter as a card, in the desk-open wizard's look. */
  const deskCard = (inst: UnitInstance, gi: number, j: number) => (
    <li key={`${gi}:${j}:${inst.code}`}>
      <button type="button" className="br-desk" onClick={() => onOpen(gi, j)}>
        <span className="br-desk__icon" aria-hidden="true">
          <Icon name="counter" size={24} />
        </span>
        <span className="br-desk__arrow" aria-hidden="true">
          <Icon name="right" size={16} />
        </span>
        <strong className="br-desk__name">{inst.name}</strong>
        <span className="br-desk__code">{inst.code}</span>
        {problemTag(gi, j)}
        <span className="br-desk__status">
          <LiveStatus kind="service_point" loaded={live.counters !== null} desk={liveDesk(inst.code)} timezone={branch.timezone} />
        </span>
      </button>
    </li>
  )

  /** A machine as a row: who it is, the design it shows, whether it runs. kind is null when it runs nowhere. */
  const row = (kind: ProfileUnitKind | null, pu: ProfileUnit | undefined, inst: UnitInstance, gi: number, j: number) => {
    const fields = kind && pu ? designFields(kind) : []
    return (
      <li key={`${gi}:${j}:${inst.code}`} className="bpm-row" data-design={fields.length > 0 || undefined}>
        <button type="button" className="bpm-row__who" disabled={!pu} onClick={() => onOpen(gi, j)}>
          <span className="set-tile bpm-code">{inst.code}</span>
          <span className="bpm-row__names">
            <span className="bpm-row__name">{inst.name}</span>
            <span className="bpm-row__sub">
              {(kind === 'desk_display' || kind === 'feedback') &&
                (inst.desk ? deskLabel(inst.desk, deskName(inst.desk)) : say('catalogue.units.chooseDesk'))}
              {kind === 'display' &&
                (inst.deskFilter?.length ? inst.deskFilter.map((c) => deskLabel(c, deskName(c))).join(', ') : say('eq.inst.allDesks'))}
            </span>
            {problemTag(gi, j)}
          </span>
        </button>
        {fields.length > 0 && kind && pu && (
          <div className="bpm-row__design">
            {layouts === null ? (
              <span className="bpm-row__wait" />
            ) : (
              fields.map((f) => (
                <DesignPicker
                  key={f}
                  md={machineDesign(f, kind, pu, inst.overrides ?? {}, equipment.kindDefaults[kind] ?? {}, layouts, draft.operationProfileId)}
                  kind={kind}
                  label={say(f === 'ticketLayoutId' ? 'eq.sheet.ticket' : 'eq.sheet.design')}
                  branchName={branch.name}
                  disabled={locked}
                  onChange={(v) => setDesign(gi, j, f, v)}
                />
              ))
            )}
          </div>
        )}
        <span className="bpm-row__live">
          {kind && (
            <LiveStatus
              kind={kind}
              loaded={kind === 'service_point' ? live.counters !== null : live.units !== null}
              unit={kind === 'service_point' ? undefined : liveUnit(inst.code)}
              desk={kind === 'service_point' ? liveDesk(inst.code) : undefined}
              timezone={branch.timezone}
            />
          )}
        </span>
        {pu && (
          <Button isIconOnly variant="ghost" size="sm" className="bpm-row__open" aria-label={inst.name} onPress={() => onOpen(gi, j)}>
            <Icon name="right" size={16} />
          </Button>
        )}
      </li>
    )
  }

  const unitHead = (pu: ProfileUnit, named = false) => {
    const gi = draft.units.findIndex((g) => g.profileUnitId === pu.id)
    const n = gi >= 0 ? draft.units[gi]!.instances.length : 0
    const custom = pu.name !== say(`eq.kind.one.${pu.kind}` as StaffKey)
    return (
      <>
        {(named || custom) && <span className="bpm-unit__name">{pu.name}</span>}
        {n !== pu.defaultCount && <span className="eq-inst__hint">{say('eq.inst.profileCount', { n: pu.defaultCount })}</span>}
        <span className="bpm-unit__step">{stepper(pu, gi, n)}</span>
      </>
    )
  }

  const kinds = MACHINE_ORDER.filter((k) => doc.units.some((u) => u.kind === k))
  // The branch's equipment profile, in Settings → Devices & displays → Equipment.
  const profileLink = readsProfiles && draft.equipmentProfileId ? `#equipment/${encodeURIComponent(draft.equipmentProfileId)}` : ''

  return (
    <FormSection
      id="bp-equipment-title"
      icon="machine"
      title={say('bpm.machines')}
      line={say('bpm.machinesLine')}
      aside={
        profileLink && kinds.length > 0 ? (
          <a className={buttonVariants({ variant: 'ghost', size: 'sm' })} href={profileLink}>
            {say('eq.inst.toProfile')} <Icon name="right" size={16} />
          </a>
        ) : undefined
      }
    >
      {kinds.length === 0 && strays.length === 0 && (
        <p className="eq-inst__empty">
          {say('eq.inst.none')}{' '}
          {profileLink && <a href={profileLink}>{say('eq.inst.addToProfile')}</a>}
        </p>
      )}

      {kinds.map((kind) => {
        const units = doc.units.filter((u) => u.kind === kind)
        const lone = units.length === 1 ? units[0]! : null
        const count = draft.units
          .filter((g) => unitOf.get(g.profileUnitId)?.kind === kind)
          .reduce((n, g) => n + g.instances.length, 0)
        return (
          <section key={kind} className="bpm-group" aria-labelledby={`bpm-${kind}`}>
            <header className="bpm-group__head">
              <h3 id={`bpm-${kind}`} className="bpm-group__title">
                {say(`eq.kind.${kind}` as StaffKey)}
              </h3>
              {!lone && <span className="bpm-group__n">{count}</span>}
              {lone && unitHead(lone)}
            </header>
            {units.map((pu) => {
              const gi = draft.units.findIndex((g) => g.profileUnitId === pu.id)
              const instances = gi >= 0 ? draft.units[gi]!.instances : []
              return (
                <div key={pu.id} className="bpm-unit">
                  {!lone && <div className="bpm-unit__head">{unitHead(pu, true)}</div>}
                  {askLine(pu.id)}
                  {instances.length > 0 &&
                    (kind === 'service_point' ? (
                      <ul className="br-desks">{instances.map((inst, j) => deskCard(inst, gi, j))}</ul>
                    ) : (
                      <ul className="bpm-list">{instances.map((inst, j) => row(kind, pu, inst, gi, j))}</ul>
                    ))}
                </div>
              )
            })}
          </section>
        )
      })}

      {strays.length > 0 && (
        <section className="bpm-group">
          <header className="bpm-group__head">
            <h3 className="bpm-group__title">
              <Chip size="sm" variant="soft" color="warning">
                {say('eq.inst.notInProfile')}
              </Chip>
            </h3>
          </header>
          {strays.map(([g, gi]) => {
            const key = `stray:${g.profileUnitId}`
            return (
              <div key={key} className="bpm-unit">
                <ul className="bpm-list">
                  {g.instances.map((inst, j) =>
                    row(liveUnit(inst.code)?.kind ?? (liveDesk(inst.code) ? 'service_point' : null), undefined, inst, gi, j),
                  )}
                </ul>
                {canWrite && ask?.key !== key && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="eq-inst__drop"
                    isDisabled={locked}
                    onPress={() =>
                      setAsk({
                        key,
                        leaving: g.instances.map((i) => {
                          const c = liveDesk(i.code)
                          return c?.staffName ? { code: i.code, staffName: c.staffName } : { code: i.code }
                        }),
                        apply: () => dropGroup(gi),
                      })
                    }
                  >
                    <Icon name="trash" size={16} /> {say('common.remove')}
                  </Button>
                )}
                {askLine(key)}
              </div>
            )
          })}
        </section>
      )}
    </FormSection>
  )
}

const DESK_STATE: Record<CounterView['state'], StaffKey> = {
  open: 'common.desk.open',
  paused: 'common.desk.paused',
  closed: 'common.desk.closed',
}

/** Running or not, and what the machine last reported; for a service point, the desk's state and who. */
export function LiveStatus({
  kind,
  loaded,
  unit,
  desk,
  timezone,
}: {
  kind: ProfileUnitKind
  loaded: boolean
  unit?: Unit
  desk?: CounterView
  timezone?: string
}) {
  const { say, lang } = useStaffText()
  if (!loaded) return <span className="eq-inst__live" />
  if (kind === 'service_point') {
    if (!desk) return <span className="eq-inst__live eq-inst__later">{say('settings.live.notSaved')}</span>
    return (
      <span className="eq-inst__live">
        <span className="eq-inst__state" data-state={desk.state} aria-hidden="true" />
        {say(DESK_STATE[desk.state])}
        {desk.staffName && <span className="eq-inst__who">{desk.staffName}</span>}
      </span>
    )
  }
  if (!unit) return <span className="eq-inst__live eq-inst__later">{say('settings.live.notSaved')}</span>

  const lag = lagOf(unit)
  const fault = printerFault(unit)
  const skip = mediaSkipped(unit)
  const cache = screenCacheOf(unit)
  const amount = (bytes: number) => {
    const a = cacheAmount(bytes, lang)
    return say(a.key, { n: a.n })
  }
  return (
    <span className="eq-inst__live">
      {unit.running ? (
        <>
          <span className="dot dot--open" aria-hidden="true" />
          {say('catalogue.units.running')}
          {lag && (
            <Chip size="sm" variant="soft" color="warning">
              {say(lag === 'app' ? 'catalogue.units.oldApp' : 'catalogue.units.oldDesign')}
            </Chip>
          )}
        </>
      ) : (
        <>
          <span className="eq-inst__state" data-state="closed" aria-hidden="true" />
          {say('catalogue.units.offline')}
        </>
      )}
      {fault && (
        <Chip size="sm" variant="soft" color="warning" title={fault.error}>
          {say(fault.absent ? 'eq.inst.noPrinter' : 'eq.inst.printerFault')} {clockIn(fault.at, timezone)}
        </Chip>
      )}
      {skip && (
        <Chip size="sm" variant="soft" color="warning" title={skip.name}>
          {say(skip.kind === 'video' ? 'eq.inst.skippedFilm' : 'eq.inst.skippedPicture')} {clockIn(skip.at, timezone)}
        </Chip>
      )}
      {cache && (
        <Chip size="sm" variant="secondary" title={clockIn(cache.at, timezone)}>
          {say('eq.inst.cache', { used: amount(cache.bytes), limit: amount(cache.limit) })}
        </Chip>
      )}
    </span>
  )
}
